import { createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import createApiClient from "../../utils/createApiClient";

export interface FleetFinancialTableEntry {
  category: string;
  value: string | number;
  _id?: string;
}

export interface FleetEnvironmentalTableEntry {
  category: string;
  value: string | number;
  _id?: string;
}

export interface TaxBenefitsTableEntry {
  category: string;
  value: string | number;
  _id?: string;
}

export interface CostOverTimeData {
  labels: string[];
  datasets: Array<{
    label: string;
    data: number[];
    _id?: string;
  }>;
}

export interface CumulativeSavingsData {
  labels: string[];
  data: number[];
}

export interface EnvironmentalData {
  labels: string[];
  data: number[];
}

export interface FleetEnergyCosts {
  energy: number[];
  energyTime: number[];
  incrementalEnergy: number[];
  demand: number[];
  demandTime: number[];
  incrementalDemand: number[];
  serviceCharge: number[];
  otherCharges: number[];
  total: number[];
}

export interface FleetUtilityDetails {
  state: string;
  zipCode: string;
  utilityName: string;
  utilityId: string;
  scheduleId: string;
  totalCost: string | number;
}

export interface FleetReportData {
  reportName: string;
  location: string;
  city: string;
  zipCode: string;
  fleetSize: number;
  netSavings: number;
  utilityRate: number;
  timestamp: string;
  disableUtilityInfo?: boolean;
  energyCosts?: FleetEnergyCosts;
  utilityDetails?: FleetUtilityDetails;
  disableUtilityEnergyInfo?: boolean;
  financialTable: FleetFinancialTableEntry[];
  taxBenefitsTable: TaxBenefitsTableEntry[];
  environmentalTable: FleetEnvironmentalTableEntry[];
  costOverTimeData: CostOverTimeData;
  cumulativeSavingsData: CumulativeSavingsData;
  environmentalData: EnvironmentalData;
  paybackPeriod: number;
  analysisYears: number;
  applyTaxBenefits: boolean;
}

export interface FleetReportResponse {
  reportName: string;
  location: string;
  city: string;
  zipCode: string;
  fleetSize: number;
  netSavings: number;
  annualSavings: number;
  utilityRate: number;
  disableUtilityInfo?: boolean;
  energyCosts?: FleetEnergyCosts;
  utilityDetails?: FleetUtilityDetails;
  disableUtilityEnergyInfo?: boolean;
  financialTable: FleetFinancialTableEntry[];
  taxBenefitsTable: TaxBenefitsTableEntry[];
  environmentalTable: FleetEnvironmentalTableEntry[];
  costOverTimeData: CostOverTimeData;
  cumulativeSavingsData: CumulativeSavingsData;
  environmentalData: EnvironmentalData;
  paybackPeriod: number;
  analysisYears: number;
  applyTaxBenefits: boolean;
  timestamp: string;
  userId: string;
  _id: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

export interface FleetReportApiResponse {
  success: boolean;
  message: string;
  data?: {
    report: FleetReportResponse;
  };
}

export interface FleetReportsApiResponse {
  success: boolean;
  message: string;
  data?: {
    reports: FleetReportResponse[];
    total: number;
    page: number;
    pages: number;
  };
}

export interface UpdateFleetReportNameResponse {
  success: boolean;
  message: string;
  data?: {
    report: FleetReportResponse;
  };
}

// UPDATE FLEET REPORT DATA ACTION
export interface UpdateFleetReportDataRequest {
  id: string;
  netSavings: number;
  annualSavings: number;
  financialTable: FleetFinancialTableEntry[];
  environmentalTable: FleetEnvironmentalTableEntry[];
  paybackPeriod: number;
  costOverTimeData?: CostOverTimeData;
  cumulativeSavingsData?: CumulativeSavingsData;
  environmentalData?: EnvironmentalData;
}

export interface UpdateFleetReportDataResponse {
  success: boolean;
  message: string;
  data?: {
    report: FleetReportResponse;
  };
}

function isAxiosError(error: unknown): error is import("axios").AxiosError {
  return (error as import("axios").AxiosError).isAxiosError !== undefined;
}

// SAVED FLEET REPORT ACTION - Updated to match handleSubmit structure
export const savedFleetReport = createAsyncThunk<
  FleetReportApiResponse,
  FleetReportData,
  { rejectValue: string }
>("fleetReport/savedFleetReport", async (reportData, { rejectWithValue }) => {
  try {
    const response = await createApiClient.post<FleetReportApiResponse>(
      "/fleet-report",
      reportData
    );

    if (response.data.success) {
      toast.success("Fleet report generated successfully!");
      return response.data;
    } else {
      toast.error(response.data.message || "Failed to create fleet report");
      return rejectWithValue(
        response.data.message || "Failed to create fleet report"
      );
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";
    if (isAxiosError(error) && error.response) {
      const data = error.response.data as any;
      if (data.error && data.error.message) {
        errorMessage = data.error.message;
      } else {
        errorMessage = data.message || "Unknown error";
      }
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

// UPDATE FLEET REPORT NAME ACTION
export const updateFleetReportNameById = createAsyncThunk<
  UpdateFleetReportNameResponse,
  { id: string; reportName: string },
  { rejectValue: string }
>(
  "fleetReport/updateFleetReportNameById",
  async ({ id, reportName }, { rejectWithValue }) => {
    try {
      const response = await createApiClient.put<UpdateFleetReportNameResponse>(
        `/fleet-report/${id}`,
        { reportName }
      );

      if (response.data.success) {
        toast.success(response.data.message || "Fleet report name updated");
        return response.data;
      } else {
        toast.error(
          response.data.message || "Failed to update fleet report name"
        );
        return rejectWithValue(
          response.data.message || "Failed to update fleet report name"
        );
      }
    } catch (error) {
      let errorMessage = "An unknown error occurred";
      if (isAxiosError(error) && error.response) {
        errorMessage =
          (error.response.data as UpdateFleetReportNameResponse).message ||
          "Unknown error";
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// GET PAST FLEET REPORTS ACTION
export const getPastFleetReports = createAsyncThunk<
  FleetReportsApiResponse,
  {
    page?: number;
    limit?: number;
    formattedDateRange?: { startDate: string; endDate: string };
  },
  { rejectValue: string }
>(
  "fleetReport/getPastFleetReports",
  async ({ page, limit, formattedDateRange }, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (page !== undefined) queryParams.append("page", page.toString());
      if (limit !== undefined) queryParams.append("limit", limit.toString());
      if (formattedDateRange?.startDate)
        queryParams.append("startDate", formattedDateRange.startDate);
      if (formattedDateRange?.endDate)
        queryParams.append("endDate", formattedDateRange.endDate);
      const query = queryParams.toString() ? `?${queryParams.toString()}` : "";

      const response = await createApiClient.get<FleetReportsApiResponse>(
        `/fleet-report${query}`
      );

      if (response.data.success) {
        return response.data;
      } else {
        toast.error(
          response.data.message || "Failed to retrieve fleet reports"
        );
        return rejectWithValue(
          response.data.message || "Failed to retrieve fleet reports"
        );
      }
    } catch (error) {
      let errorMessage = "An unknown error occurred";
      if (isAxiosError(error) && error.response) {
        errorMessage =
          (error.response.data as FleetReportsApiResponse).message ||
          "Unknown error";
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// GET SPECIFIC USER FLEET REPORTS
export const getSpecificUserFleetReports = createAsyncThunk<
  FleetReportsApiResponse,
  { id: string; page?: number; limit?: number },
  { rejectValue: string }
>(
  "fleetReport/getSpecificUserFleetReports",
  async ({ id, page, limit }, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (page !== undefined) queryParams.append("page", page.toString());
      if (limit !== undefined) queryParams.append("limit", limit.toString());
      const query = queryParams.toString() ? `?${queryParams.toString()}` : "";

      const response = await createApiClient.get<FleetReportsApiResponse>(
        `/fleet-report/specific-user/${id}${query}`
      );

      if (response.data.success) {
        return response.data;
      } else {
        toast.error(
          response.data.message || "Failed to retrieve fleet reports"
        );
        return rejectWithValue(
          response.data.message || "Failed to retrieve fleet reports"
        );
      }
    } catch (error) {
      let errorMessage = "An unknown error occurred";
      if (isAxiosError(error) && error.response) {
        errorMessage =
          (error.response.data as FleetReportsApiResponse).message ||
          "Unknown error";
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// DELETE FLEET REPORT ACTION
export const deleteFleetReportById = createAsyncThunk<
  { success: boolean; message: string; data?: { id: string } },
  string,
  { rejectValue: string }
>("fleetReport/deleteFleetReportById", async (id, { rejectWithValue }) => {
  try {
    const response = await createApiClient.delete<{
      success: boolean;
      message: string;
      data?: { id: string };
    }>(`/fleet-report/${id}`);

    if (response.data.success) {
      toast.success(
        response.data.message || "Fleet report deleted successfully"
      );
      return response.data;
    } else {
      toast.error(response.data.message || "Failed to delete fleet report");
      return rejectWithValue(
        response.data.message || "Failed to delete fleet report"
      );
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";
    if (isAxiosError(error) && error.response) {
      errorMessage = (error.response.data as any).message || "Unknown error";
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

export const updateFleetReportData = createAsyncThunk<
  UpdateFleetReportDataResponse,
  UpdateFleetReportDataRequest,
  { rejectValue: string }
>(
  "fleetReport/updateFleetReportData",
  async (updateData, { rejectWithValue }) => {
    try {
      const response = await createApiClient.put<UpdateFleetReportDataResponse>(
        `/fleet-report/financial-table/${updateData.id}`,
        updateData
      );

      if (response.data.success) {
        toast.success(
          response.data.message || "Fleet report updated successfully"
        );
        return response.data;
      } else {
        toast.error(response.data.message || "Failed to update fleet report");
        return rejectWithValue(
          response.data.message || "Failed to update fleet report"
        );
      }
    } catch (error) {
      let errorMessage = "An unknown error occurred";
      if (isAxiosError(error) && error.response) {
        errorMessage =
          (error.response.data as UpdateFleetReportDataResponse).message ||
          "Unknown error";
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);
